import { isSSR } from './helpers';
import { getClientSideLanguage } from './i18n';

export const APP_PREFIX = `${isSSR() ? process.env.BRAND_NAME : process.env.NEXT_PUBLIC_BRAND_NAME}:`;
export const StorageHelperKeys = {
    ACTION_AFTER_SUBMIT: `${APP_PREFIX}actionAfterSubmit`,
    LNG: `${APP_PREFIX}lng`,
    LOGGED_OUT: `${APP_PREFIX}loggedOut`,
    NEW_USER: `${APP_PREFIX}newUser`,
    REFRESHING_TOKEN: `${APP_PREFIX}refreshingToken`,
    STOP_ALL_REQUEST: `${APP_PREFIX}stopAllRequest`,
    TENANT_ID: `${APP_PREFIX}tenantId`,
    TICKET_FIELDS: `${APP_PREFIX}ticketFields`,
    OPEN_TAB_KEYS: `${APP_PREFIX}openTabKeys`,
    CUSTOM_FIELD_SAVE_TYPE: `${APP_PREFIX}customFieldSaveType`,
    USER_FIELDS: `${APP_PREFIX}userFields`,
    TAB_ID: `${APP_PREFIX}TabId`,
    CHAT_MESSAGES_HISTORY: `${APP_PREFIX}chatMessagesHistory`,
    REGISTRATION_SESSION_ID: `${APP_PREFIX}registrationSessionId`,
    BULK_TICKET_CHANGE_JOB_LIST: `${APP_PREFIX}bulkChanges`,
    ACTIVE_BULK_TICKET_CHANGE_JOB: `${APP_PREFIX}activeBulkChange`,
    USER_LOGOUT: `${APP_PREFIX}userLogout`,
    SHOW_QUICK_FLOWS: `${APP_PREFIX}showQuickFlows`
} as const;
const ULAK_SESSION_STORAGE_KEY_PREFIX = 'UlakLog';

export const addAppPrefixToStorageKey: (key: string) => string = (key) => `${APP_PREFIX}${key}`;

export type storeItemType = (typeof StorageHelperKeys)[keyof typeof StorageHelperKeys];
export const getLocalStorageItem: (itemKey: storeItemType) => any = (itemKey) =>
    JSON.parse(window.localStorage.getItem(itemKey) ?? null);

export const setLocalStorageItem: (itemKey: storeItemType, value: any) => void = (itemKey, value) =>
    window.localStorage.setItem(itemKey, JSON.stringify(value));

export const getSessionStorageItem: (itemKey: string) => any = (itemKey) =>
    JSON.parse(window.sessionStorage.getItem(itemKey) ?? null);

export const setSessionStorageItem: (itemKey: string, value: any) => void = (itemKey, value) => {
    try {
        window.sessionStorage.setItem(itemKey, JSON.stringify(value));
    } catch (e) {
        clearUlakLogs();
        console.error('Error while setting sessionStorage item, resetting Ulak logs!', e);
        try {
            window.sessionStorage.setItem(itemKey, JSON.stringify(value));
        } catch (error) {
            clearUlakLogs();
            console.error('Error while setting sessionStorage item attempt again, ignore this action!', e);
            return;
        }
    }
};

export const removeLocaleStorageItems: () => void = () => {
    const currentLNG = getLocalStorageItem(StorageHelperKeys.LNG);
    window.localStorage.clear();
    setLocalStorageItem(StorageHelperKeys.LNG, currentLNG ?? getClientSideLanguage());
};
export const removeSessionStorageItems: () => void = () => window.sessionStorage.clear();
export const removeSessionStorageItem: (itemKey: string) => void = (itemKey: string) =>
    window.sessionStorage.removeItem(itemKey);
export const removeAllStorageItems: () => void = () => {
    removeSessionStorageItems();
    removeLocaleStorageItems();
};

/**
 * @description This function is used to clear all ulak logs from session storage except the last one.
 */
const clearUlakLogs: () => void = () => {
    // filter and sort ulak logs
    const ulakKeys = Object.keys(window.sessionStorage)
        .filter((f) => f.startsWith(ULAK_SESSION_STORAGE_KEY_PREFIX))
        .sort((a, b) => `${a}`.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));

    // discard last log from the list
    ulakKeys.splice(ulakKeys.length - 1, 1);

    // remove all ulak logs from session storage
    ulakKeys.forEach((key) => window.sessionStorage.removeItem(key));
};
